import {
    getOwnedNodes,
    NumberInput,
    NumberExport,
    NumberValue,
    MaterialInput,
    TemplateGraphInput,
    getTemplateNodeClassLabel,
    MathOperator,
    ObjectPathPart,
    MoveMaterial,
} from "@cm/template-nodes"
import {isInstanceOf, uuid4} from "@cm/utils"
import {ChangeDetectionStrategy, Component, computed, effect, output, signal, input, inject} from "@angular/core"
import {NgTemplateOutlet} from "@angular/common"
import {MatTooltip} from "@angular/material/tooltip"
import {MatMenuModule} from "@angular/material/menu"
import {BaseInspectorComponent} from "@template-editor/components/inspectors/base-inspector/base-inspector.component"
import {
    MattressComponentGraph,
    MattressGroup,
    MattressMesh,
    MattressCurve,
    MattressCurveInput,
    MattressReferenceFrameInput,
    MattressComputedCurve,
    MattressComponentGraphGenerator,
    MattressCurveExport,
    MattressReferenceFrameExport,
    MattressCut,
    MattressComponentInstance,
    MattressReferenceFrame,
    MattressNodes,
    MattressNode,
    isMattressComponentGraphNode,
} from "@cm/mattress-nodes"
import type {MattressComponentGraphNode, MattressFocusedComponentInstancePath} from "@cm/mattress-nodes"

const isMattressNode = isInstanceOf(MattressNode)

import {ListItemComponent} from "@common/components/item/list-item/list-item.component"
import {TemplateMenuComponent} from "@template-editor/components/template-menu/template-menu.component"
import {TemplateMenuSectionComponent} from "@template-editor/components/template-menu-section/template-menu-section.component"
import {getNodeIconClass} from "@app/template-editor/helpers/template-icons"
import {MattressInspectorService} from "@template-editor/components/inspectors/mattress-inspector/mattress-inspector.service"
import {TemplateNodeDragService} from "@app/template-editor/services/template-node-drag.service"

type MattressNodeContainer = MattressComponentGraph | MattressGroup

type MattressNodeItem = {
    node: MattressComponentGraphNode
    name: string
    isSelected: boolean
    isActive: boolean
    iconClass: string
    isExpandable: boolean
    isExpanded: boolean
    isComponentGraph: boolean
    subItems: MattressNodeItem[]
}

@Component({
    selector: "cm-mattress-component-graph-inspector",
    templateUrl: "./mattress-component-graph-inspector.component.html",
    styleUrl: "./mattress-component-graph-inspector.component.scss",
    imports: [MatTooltip, MatMenuModule, NgTemplateOutlet, ListItemComponent, TemplateMenuComponent, TemplateMenuSectionComponent],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MattressComponentGraphInspectorComponent extends BaseInspectorComponent<MattressComponentGraph> {
    readonly drag = inject(TemplateNodeDragService)

    mattressInspectorService = inject(MattressInspectorService)

    readonly $selectedNodes = computed(() => {
        const nodesItems = this.$nodesItems().map((item) => item.node)
        const selectedObjectPathParts = this.sceneManagerService.$selectedObjectPathParts()
        return nodesItems.filter((node) => selectedObjectPathParts.some((part) => part.path.includes(node)))
    })
    readonly $inspectedNode = input<MattressComponentGraphNode | undefined>(undefined, {alias: "inspectedNode"})

    readonly $nodes = computed<MattressComponentGraphNode[]>(() => {
        this.mattressInspectorService.$mattressParameters()
        return this.getGraphItems(this.$node())
    })
    readonly $focusedComponentInstancePath = input<MattressFocusedComponentInstancePath | undefined>(undefined, {alias: "focusedComponentInstancePath"})
    readonly focusedComponentInstancePathChanged = output<MattressFocusedComponentInstancePath>()

    private $_expandedNodes = signal<Set<MattressComponentGraphNode>>(new Set())
    toggleExpandedNode(node: MattressComponentGraphNode) {
        this.$_expandedNodes.update((oldSet) => {
            const newSet = new Set(oldSet)
            if (newSet.has(node)) newSet.delete(node)
            else newSet.add(node)
            return newSet
        })
    }

    constructor() {
        super()

        effect(() => {
            const selectedObjectPathParts = this.sceneManagerService.$selectedObjectPathParts()
            if (selectedObjectPathParts.length === 0) return

            const nodesToExpand = new Set<MattressComponentGraphNode>()
            const findAncestorsOfSelected = (nodes: MattressComponentGraphNode[], ancestors: MattressComponentGraphNode[]): boolean => {
                let hasSelectedDescendant = false
                for (const node of nodes) {
                    if (selectedObjectPathParts.some((part) => part.path.includes(node))) {
                        hasSelectedDescendant = true
                        ancestors.forEach((a) => nodesToExpand.add(a))
                    }
                    if (node instanceof MattressComponentGraph || node instanceof MattressGroup) {
                        const children = getOwnedNodes(node).filter(isMattressComponentGraphNode)
                        if (findAncestorsOfSelected(children, [...ancestors, node])) {
                            hasSelectedDescendant = true
                            nodesToExpand.add(node)
                        }
                    }
                }
                return hasSelectedDescendant
            }
            findAncestorsOfSelected(this.$nodes(), [])

            if (nodesToExpand.size > 0) {
                this.$_expandedNodes.update((oldSet) => {
                    const newSet = new Set(oldSet)
                    nodesToExpand.forEach((node) => newSet.add(node))
                    return newSet
                })
            }
        })
    }

    onMouseEnter(componentGraph: MattressComponentGraph, node: MattressComponentGraphNode) {
        const indirectHoverItems = componentGraph
            .getParentMattressNode()
            .getSceneNodePartsForParameterNodePart({templateNode: node, part: "root"}, this.sceneManagerService)
            .map<ObjectPathPart>((sceneNodePart) => ({path: sceneNodePart.sceneNode.path, part: sceneNodePart.part}))

        this.sceneManagerService.hoverNodes([{path: [componentGraph.getParentMattressNode(), node], part: "root"}, ...indirectHoverItems])
    }

    onMouseLeave(_node: MattressComponentGraphNode) {
        this.sceneManagerService.hoverNodes(undefined)
    }

    getMattressNodesItems(
        nodes: MattressComponentGraphNode[],
        selectedObjectPathParts: ObjectPathPart[],
        expandedNodes: Set<MattressComponentGraphNode>,
        traverseNestedGraph: boolean,
    ): MattressNodeItem[] {
        return nodes.map((node) => {
            const {isComponentGraph, subItems} = (() => {
                if (node instanceof MattressComponentGraph && traverseNestedGraph)
                    return {
                        isComponentGraph: true,
                        subItems: this.getMattressNodesItems(
                            getOwnedNodes(node).filter(isMattressComponentGraphNode),
                            selectedObjectPathParts,
                            expandedNodes,
                            false,
                        ),
                    }
                if (node instanceof MattressGroup)
                    return {
                        isComponentGraph: true,
                        subItems: this.getMattressNodesItems(
                            getOwnedNodes(node).filter(isMattressComponentGraphNode),
                            selectedObjectPathParts,
                            expandedNodes,
                            traverseNestedGraph,
                        ),
                    }
                return {isComponentGraph: false, subItems: []}
            })()

            const params = node.parameters as {name?: string; value?: unknown}
            const name = params.name ?? (params.value !== undefined ? String(params.value) : node.nodeClass)

            return {
                node,
                name,
                isSelected: selectedObjectPathParts.some((part) => part.path.includes(node)),
                isActive: this.sceneManagerService.isNodeActive(node),
                iconClass: getNodeIconClass(node.nodeClass),
                class: getTemplateNodeClassLabel(node),
                isExpandable: isComponentGraph,
                isExpanded: expandedNodes.has(node),
                isComponentGraph,
                subItems,
            }
        })
    }

    readonly $nodesItems = computed<MattressNodeItem[]>(() => {
        const nodes = this.$nodes()
        const selectedObjectPathParts = this.sceneManagerService.$selectedObjectPathParts()
        const expandedNodes = this.$_expandedNodes()
        return this.getMattressNodesItems(nodes, selectedObjectPathParts, expandedNodes, true)
    })

    readonly $referenceFrameIcon = computed(() => getNodeIconClass(MattressReferenceFrame.getNodeClass()))
    readonly $referenceFrameInputIcon = computed(() => getNodeIconClass(MattressReferenceFrameInput.getNodeClass()))
    readonly $referenceFrameExportIcon = computed(() => getNodeIconClass(MattressReferenceFrameExport.getNodeClass()))
    readonly $curveIcon = computed(() => getNodeIconClass(MattressCurve.getNodeClass()))
    readonly $curveInputIcon = computed(() => getNodeIconClass(MattressCurveInput.getNodeClass()))
    readonly $curveExportIcon = computed(() => getNodeIconClass(MattressCurveExport.getNodeClass()))
    readonly $computedCurveIcon = computed(() => getNodeIconClass(MattressComputedCurve.getNodeClass()))
    readonly $cutIcon = computed(() => getNodeIconClass(MattressCut.getNodeClass()))
    readonly $numberInputIcon = computed(() => getNodeIconClass(NumberInput.getNodeClass()))
    readonly $materialInputIcon = computed(() => getNodeIconClass(MaterialInput.getNodeClass()))
    readonly $numberValueIcon = computed(() => getNodeIconClass(NumberValue.getNodeClass()))
    readonly $mathOperatorIcon = computed(() => getNodeIconClass(MathOperator.getNodeClass()))
    readonly $numberExportIcon = computed(() => getNodeIconClass(NumberExport.getNodeClass()))
    readonly $templateInputIcon = computed(() => getNodeIconClass(TemplateGraphInput.getNodeClass()))
    readonly $componentInstanceIcon = computed(() => getNodeIconClass(MattressComponentInstance.getNodeClass()))
    readonly $componentGraphIcon = computed(() => getNodeIconClass(MattressComponentGraph.getNodeClass()))
    readonly $componentGraphGeneratorIcon = computed(() => getNodeIconClass(MattressComponentGraphGenerator.getNodeClass()))
    readonly $groupIcon = computed(() => getNodeIconClass(MattressGroup.getNodeClass()))
    readonly $moveMaterialIcon = computed(() => getNodeIconClass(MoveMaterial.getNodeClass()))

    selectNode(componentGraph: MattressComponentGraph, node: MattressComponentGraphNode, mouseEvent: MouseEvent) {
        const part = "root"
        const objectPathPart: ObjectPathPart = {path: [componentGraph.getParentMattressNode(), node], part}
        const additionalParts: ObjectPathPart[] = componentGraph
            .getParentMattressNode()
            .getSceneNodePartsForParameterNodePart({templateNode: node, part: "root"}, this.sceneManagerService)
            .map((sceneNodePart) => ({path: sceneNodePart.sceneNode.path, part: sceneNodePart.part}))
        const base = mouseEvent.shiftKey ? this.sceneManagerService.$selectedObjectPathParts() : []
        this.sceneManagerService.selectNodes([...base, objectPathPart, ...additionalParts])
    }

    setFocusedComponentInstancePath(instance: MattressComponentInstance) {
        const currentPath = this.$focusedComponentInstancePath()
        if (currentPath === undefined) throw new Error("Focused component instance path is undefined")
        this.focusedComponentInstancePathChanged.emit(currentPath === "root" ? [instance] : [...currentPath, instance])
    }

    addMattressCurve(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph).filter(isMattressNode)
        const curveCount = items.filter((n) => n instanceof MattressCurve).length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newCurve = new MattressCurve({
                parameters: {
                    name: `Curve ${curveCount + 1}`,
                    controlPoints: [],
                    closed: false,
                    resolution: 1.0,
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newCurve],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMattressCurveInput(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph).filter(isMattressNode)
        const inputCount = items.filter((n) => n instanceof MattressCurveInput).length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newInput = new MattressCurveInput({
                parameters: {
                    name: `Curve Input ${inputCount + 1}`,
                    id: uuid4(),
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newInput],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMattressReferenceFrameInput(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph).filter(isMattressNode)
        const inputCount = items.filter((n) => n instanceof MattressReferenceFrameInput).length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newInput = new MattressReferenceFrameInput({
                parameters: {
                    name: `Reference Frame Input ${inputCount + 1}`,
                    id: uuid4(),
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newInput],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMattressComputedCurve(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph).filter(isMattressNode)
        const computedCurveCount = items.filter((n) => n instanceof MattressComputedCurve).length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newComputedCurve = new MattressComputedCurve({
                parameters: {
                    name: `Computed Curve ${computedCurveCount + 1}`,
                    parameters: {
                        type: "MattressComputedCurveSwapReferenceFrame",
                        curve: null,
                        oldReferenceFrame: null,
                        newReferenceFrame: null,
                    },
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newComputedCurve],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMattressComponentGraphGenerator(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph).filter(isMattressNode)
        const generatorCount = items.filter((n) => n instanceof MattressComponentGraphGenerator).length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newGenerator = new MattressComponentGraphGenerator({
                parameters: {
                    name: `Component Graph Generator ${generatorCount + 1}`,
                    parameters: {
                        type: "MattressComponentGraphGeneratorUnitRepeat1D",
                        unitGraph: null,
                        unitCount: null,
                        unitIdPrefix: null,
                        unitInputs: {},
                        baseGraph: null,
                    },
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newGenerator],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMattressCurveExport(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph).filter(isMattressNode)
        const exportCount = items.filter((n) => n instanceof MattressCurveExport).length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newExport = new MattressCurveExport({
                parameters: {
                    name: `Curve Output ${exportCount + 1}`,
                    id: uuid4(),
                    node: null,
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newExport],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMattressReferenceFrameExport(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph).filter(isMattressNode)
        const exportCount = items.filter((n) => n instanceof MattressReferenceFrameExport).length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newExport = new MattressReferenceFrameExport({
                parameters: {
                    name: `Reference Frame Output ${exportCount + 1}`,
                    id: uuid4(),
                    node: null,
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newExport],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMattressCut(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph).filter(isMattressNode)
        const cutCount = items.filter((n) => n instanceof MattressCut).length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newCut = new MattressCut({
                parameters: {
                    name: `Cut ${cutCount + 1}`,
                    conditions: [],
                    materialAssignment: null,
                    fabricLayout: "Waterfall",
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newCut],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMattressComponentInstance(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph).filter(isMattressNode)
        const instanceCount = items.filter((n) => n instanceof MattressComponentInstance).length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newInstance = new MattressComponentInstance({
                parameters: {
                    name: `Component Instance ${instanceCount + 1}`,
                    id: uuid4(),
                    graph: null,
                    parameters: {},
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newInstance],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    duplicateMattressComponentInstance(componentGraph: MattressNodeContainer, instance: MattressComponentInstance) {
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newInstance = new MattressComponentInstance({
                parameters: {
                    name: `Copy of ${instance.parameters.name}`,
                    id: uuid4(),
                    graph: instance.parameters.graph,
                    parameters: {...instance.parameters.parameters},
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newInstance],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addNumberInput(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph, NumberInput)
        const inputCount = items.length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const name = `Number Input ${inputCount + 1}`
            const newInput = new NumberInput({
                parameters: {
                    name,
                    id: uuid4(),
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newInput],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMaterialInput(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph, MaterialInput)
        const inputCount = items.length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const name = `Material Input ${inputCount + 1}`
            const newInput = new MaterialInput({
                parameters: {
                    name,
                    id: uuid4(),
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newInput],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMoveMaterial(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph, MoveMaterial)
        const count = items.length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newNode = new MoveMaterial({
                parameters: {
                    name: `Move Material ${count + 1}`,
                    material: null,
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newNode],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addTemplateInput(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph, TemplateGraphInput)
        const inputCount = items.length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const name = `Template Input ${inputCount + 1}`
            const newInput = new TemplateGraphInput({
                parameters: {
                    name,
                    id: uuid4(),
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newInput],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addNumberValue(componentGraph: MattressNodeContainer) {
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newValue = new NumberValue({parameters: {value: 0}})
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newValue],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMathOperator(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph, MathOperator)
        const count = items.length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newOperator = new MathOperator({
                parameters: {
                    name: `Math Operator ${count + 1}`,
                    type: "add",
                    inputA: null,
                    inputB: null,
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newOperator],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addNumberExport(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph, NumberExport)
        const exportCount = items.length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const name = `Number Output ${exportCount + 1}`
            const newExport = new NumberExport({
                parameters: {
                    name,
                    id: uuid4(),
                    node: null,
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newExport],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMattressReferenceFrame(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph).filter(isMattressNode)
        const referenceFrameCount = items.filter((n) => n instanceof MattressReferenceFrame).length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newReferenceFrame = new MattressReferenceFrame({
                parameters: {
                    name: `Reference Frame ${referenceFrameCount + 1}`,
                    type: "zAxisRing",
                    parent: "global",
                    origin: null,
                    unit: null,
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newReferenceFrame],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMattressComponentGraph(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph).filter(isMattressNode)
        const graphCount = items.filter((n) => n instanceof MattressComponentGraph).length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newGraph = new MattressComponentGraph({
                parameters: {
                    name: `Component Graph ${graphCount + 1}`,
                    nodes: new MattressNodes({parameters: {list: []}}),
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newGraph],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    addMattressGroup(componentGraph: MattressNodeContainer) {
        const items = getOwnedNodes(componentGraph).filter(isMattressNode)
        const groupCount = items.filter((n) => n instanceof MattressGroup).length
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newGroup = new MattressGroup({
                parameters: {
                    name: `Group ${groupCount + 1}`,
                    nodes: new MattressNodes({parameters: {list: []}}),
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...getOwnedNodes(componentGraph), newGroup],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    nodeDragData: {
        draggedNode: MattressComponentGraphNode
        sourceGraph: MattressNodeContainer
        startIndex: number
        targetGraph: MattressNodeContainer
        proposedEndIndex: number
    } | null = null

    private getGraphItems(graph: MattressNodeContainer): MattressComponentGraphNode[] {
        const nodes = getOwnedNodes(graph).filter(isMattressComponentGraphNode)
        const meshNodes = nodes.filter(isInstanceOf(MattressMesh))
        return [...meshNodes, ...nodes.filter((n) => !(n instanceof MattressMesh))]
    }

    nodeDragStart(event: DragEvent, node: MattressComponentGraphNode, sourceGraph: MattressNodeContainer) {
        event.stopPropagation()
        if (node instanceof MattressMesh) return
        if (this.nodeDragData) throw new Error("Attempting to start node drag while already dragging")

        const items = this.getGraphItems(sourceGraph)
        const index = items.indexOf(node)
        if (index === -1) throw new Error("Failed to find index of the dragged node")

        this.drag.dragStart(event, node)
        this.nodeDragData = {draggedNode: node, sourceGraph, startIndex: index, targetGraph: sourceGraph, proposedEndIndex: index}
    }

    nodeDragOver(event: DragEvent, node: MattressComponentGraphNode, targetGraph: MattressNodeContainer) {
        event.preventDefault()
        if (!this.nodeDragData) return

        const items = this.getGraphItems(targetGraph)
        const index = items.indexOf(node)
        if (index === -1) return

        this.nodeDragData.targetGraph = targetGraph
        if (targetGraph === this.nodeDragData.sourceGraph) {
            this.nodeDragData.proposedEndIndex = this.nodeDragData.startIndex < index ? index + 1 : index
        } else {
            this.nodeDragData.proposedEndIndex = index
        }
    }

    nodeDragEnd(event: DragEvent) {
        event.stopPropagation()
        if (!this.nodeDragData) throw new Error("Attempting to end node drag without starting drag")
        const dragData = this.nodeDragData
        this.nodeDragData = null

        if (dragData.proposedEndIndex === -1) return
        if (dragData.sourceGraph === dragData.targetGraph && dragData.startIndex === dragData.proposedEndIndex) return

        if (dragData.sourceGraph === dragData.targetGraph) {
            const items = [...this.getGraphItems(dragData.sourceGraph)]
            if (dragData.startIndex < dragData.proposedEndIndex) {
                items.splice(dragData.proposedEndIndex, 0, dragData.draggedNode)
                items.splice(dragData.startIndex, 1)
            } else {
                items.splice(dragData.startIndex, 1)
                items.splice(dragData.proposedEndIndex, 0, dragData.draggedNode)
            }
            this.sceneManagerService.modifyTemplateGraph(() => {
                dragData.sourceGraph.parameters.nodes.updateParameters({list: items})
            })
        } else {
            const sourceItems = this.getGraphItems(dragData.sourceGraph).filter((n) => n !== dragData.draggedNode)
            const targetItems = [...this.getGraphItems(dragData.targetGraph)]
            targetItems.splice(dragData.proposedEndIndex, 0, dragData.draggedNode)
            this.sceneManagerService.modifyTemplateGraph(() => {
                dragData.sourceGraph.parameters.nodes.updateParameters({list: sourceItems})
                dragData.targetGraph.parameters.nodes.updateParameters({list: targetItems})
            })
        }

        this.mattressInspectorService.onMattressParametersUpdated()
    }

    nodeDragOverChildNodes(event: DragEvent, group: MattressNodeContainer) {
        event.preventDefault()
        if (!this.nodeDragData) return
        const items = this.getGraphItems(group)
        this.nodeDragData.targetGraph = group
        this.nodeDragData.proposedEndIndex = items.length
    }

    nodeDragLeaveInspector(event: DragEvent) {
        event.preventDefault()
        if (!this.nodeDragData) return
        // Only reset when actually leaving the inspector container, not when dragleave bubbles from a child element
        const container = event.currentTarget as HTMLElement
        const relatedTarget = event.relatedTarget as Node | null
        if (relatedTarget && container.contains(relatedTarget)) return
        this.nodeDragData.proposedEndIndex = -1
    }

    createComponentGraphFromSelection(componentGraph: MattressNodeContainer) {
        const selectedNodes = this.$selectedNodes()
        if (selectedNodes.length === 0) return

        const currentList = getOwnedNodes(componentGraph)
        const selectedSet = new Set<MattressComponentGraphNode>(selectedNodes)
        const nodesToMove = currentList.filter((n) => selectedSet.has(n))
        if (nodesToMove.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() => {
            const newGraph = new MattressComponentGraph({
                parameters: {
                    name: "Component Graph",
                    nodes: new MattressNodes({parameters: {list: nodesToMove}}),
                },
            })
            componentGraph.parameters.nodes.updateParameters({
                list: [...currentList.filter((n) => !selectedSet.has(n)), newGraph],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    deleteNode(componentGraph: MattressComponentGraph, node: MattressComponentGraphNode) {
        this.sceneManagerService.modifyTemplateGraph(() => {
            componentGraph.parameters.nodes.updateParameters({
                list: getOwnedNodes(componentGraph).filter((n) => n !== node),
            })
        })
        this.sceneManagerService.selectNode({path: [componentGraph.getParentMattressNode(), componentGraph], part: "root"})
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    canEditInInstanceContext(node: MattressComponentInstance) {
        return node.parameters.graph ? node.parameters.graph instanceof MattressComponentGraph : false
    }

    isMattressComponentInstance = isInstanceOf(MattressComponentInstance)
    isMattressComponentGraph = isInstanceOf(MattressComponentGraph)
    isMattressNode = isMattressNode
    isMattressMesh = isInstanceOf(MattressMesh)
}
